import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MailCheck } from "lucide-react";
import * as Api from "../lib/api";
import { AuthShell, Field } from "./LoginPage";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await Api.requestPasswordReset(email);
      setSent(true);
    } catch (err) {
      // Backend is enumeration-safe and always 200s, but if the request itself
      // fails (network / 5xx) we surface a generic error.
      toast.error(err?.response?.data?.detail || "Could not send reset email — try again.");
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <AuthShell title="Check Your Inbox" subtitle="">
        <div className="space-y-5" data-testid="forgot-password-success">
          <div className="bg-black border-2 border-[#F26522] p-5 flex items-start gap-3">
            <MailCheck className="w-6 h-6 text-[#F26522] flex-shrink-0 mt-0.5" />
            <div className="text-white/85 text-sm leading-relaxed">
              If an account exists for that email address, a password reset link has been sent.
            </div>
          </div>
          <div className="text-white/50 text-xs leading-relaxed">
            Link expires in 60 minutes and can be used once. Check spam if it doesn't arrive within a minute.
          </div>
          <Link
            to="/login"
            data-testid="forgot-password-back-to-login"
            className="block w-full h-14 border-2 border-white text-white text-center leading-[3.25rem] font-heading font-black tracking-wider uppercase text-base hover:bg-white hover:text-black transition-colors">
            Back to Log In
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Forgot Password" subtitle="We'll email a one-time reset link">
      <form onSubmit={submit} className="space-y-5" data-testid="forgot-password-form">
        <Field
          label="Account email"
          type="email"
          value={email}
          onChange={setEmail}
          testId="forgot-password-email"
          required
          autoComplete="email"
        />
        <button
          type="submit"
          disabled={busy || !email}
          data-testid="forgot-password-submit"
          className="w-full h-16 bg-[#F26522] text-black font-heading font-black tracking-wider uppercase text-base flex items-center justify-center gap-2 hover:bg-[#FF7A2A] transition-colors disabled:opacity-60">
          {busy && <Loader2 className="w-5 h-5 animate-spin" />}
          Send Reset Link
        </button>
        <div className="text-center pt-4 border-t border-white/10">
          <Link to="/login" data-testid="forgot-password-cancel" className="text-[#F26522] hover:underline font-bold text-sm">
            ← Back to log in
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}
